import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import images from '../../../Utils/images';
import colors from '../../../Utils/colors';
import CheerioButton from '../../../Components/CheerioButton';
import {
    getMetaAdsUserInfo,
    getMetauserBusinessInfo,
    saveFbToken,
    saveLeadPageId,
    getUserApi,
    getLeadPages,
    onboardTwoTierBusiness,
    delinkFbToken,
} from '../../../Services';

const SetupMetaAds = () => {
    const localdata = useSelector((state) => state.main.localdata);
    const authtoken = useSelector((state) => state.main.auth_token);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [currentStep, setCurrentStep] = useState('connect');
    const [availablePages, setAvailablePages] = useState([]);
    const [selectedPageId, setSelectedPageId] = useState('');
    const [businessId, setBusinessId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPageForBM, setSelectedPageForBM] = useState('');
    const [childBMId, setChildBMId] = useState('');
    const [isConnected, setIsConnected] = useState(false);

    useEffect(() => {
        // Check if already connected
        if (localdata?.metaConfig?.leadsPageId) {
            setIsConnected(true);
            // Check if child BM is also created
            if (localdata?.metaConfig?.childBMId && localdata?.metaConfig?.isChildBMAdded) {
                setCurrentStep('bmSuccess');
                setChildBMId(localdata.metaConfig.childBMId);
            } else {
                // Only leads connected, show BM creation step
                setCurrentStep('createBM');
                fetchPagesForChildBM();
            }
        }
    }, [localdata, navigate]);

    const handleDisconnect = () => {
        delinkFbToken(authtoken)
            .then((res) => {
                if (res?.flag) {
                    getUserApiCall();
                    setIsConnected(false);
                    setCurrentStep('connect');
                    setAvailablePages([]);
                    setSelectedPageId('');
                    setBusinessId('');
                    setSelectedPageForBM('');
                    setChildBMId('');
                    toast.success("Successfully disconnected Meta Ads Manager", { position: 'top-center' });
                } else {
                    toast.error(res?.message || "Failed to disconnect Meta Ads Manager", { position: 'top-center' });
                }
            })
            .catch((error) => {
                console.error("Error disconnecting Meta Ads Manager:", error);
                toast.error("Failed to disconnect Meta Ads Manager", { position: 'top-center' });
            });
    };

    const handleFacebookConnect = () => {
        if (!window.FB) {
            toast.error("Facebook SDK not loaded. Please refresh the page.", { position: 'top-center' });
            return;
        }

        window.FB.login(
            function (response) {
                console.log({ response });
                console.log('WAITING FOR FACEBOOK TOKEN');

                if (response.authResponse) {
                    console.log('RECEIVED FACEBOOK TOKEN', response.authResponse);
                    const fbToken = response.authResponse.accessToken;

                    saveFbToken({ token: fbToken }, authtoken)
                        .then((res) => {
                            console.log('SAVED FACEBOOK TOKEN');
                            if (res?.flag) {
                                toast.success('Facebook connected successfully!', {
                                    autoClose: 2000,
                                    position: 'top-center'
                                });

                                // Fetch user info and pages
                                fetchUserInfoAndPages();
                            } else {
                                toast.error(res?.message, { position: 'top-center' });
                            }
                        })
                        .catch((e) => {
                            console.error('Error saving Facebook token:', e);
                            toast.error('Failed to save Facebook token', { position: 'top-center' });
                        });
                } else {
                    console.log('User cancelled login or did not fully authorize.');
                    toast.warn('Login cancelled or not fully authorized', { position: 'top-center' });
                }
            },
            {
                scope: 'pages_show_list,ads_management,leads_retrieval,pages_read_engagement,read_insights,pages_manage_cta,ads_read,whatsapp_business_management,pages_read_user_content,pages_manage_engagement,whatsapp_business_messaging',
            }
        );
    };

    const fetchUserInfoAndPages = () => {
        getMetaAdsUserInfo(authtoken).then((res) => {
            if (res?.flag && res?.data?.id) {
                const userId = res.data.id;

                // Fetch business info with pages
                const data = { userMetaId: userId };
                getMetauserBusinessInfo(data, authtoken)
                    .then((businessRes) => {
                        if (businessRes?.flag) {
                            if (businessRes?.data?.data && Array.isArray(businessRes.data.data)) {
                                setAvailablePages(businessRes.data.data);
                                setCurrentStep('selectPage');
                            } else {
                                toast.error('No pages found. Please make sure you have Facebook pages.', {
                                    position: 'top-center'
                                });
                            }
                        } else {
                            toast.error(businessRes?.message, { position: 'top-center' });
                        }
                    })
                    .catch((err) => {
                        console.error('Error fetching business info:', err);
                        toast.error('Failed to fetch Facebook pages', { position: 'top-center' });
                    });
            } else {
                toast.error(res?.message || 'Failed to get user information', { position: 'top-center' });
            }
        }).catch((err) => {
            console.error('Error fetching user info:', err);
            toast.error('Failed to fetch user information', { position: 'top-center' });
        });
    };

    const fetchPagesForChildBM = () => {
        getLeadPages(authtoken).then((res) => {
            try {
                if (res?.flag) {
                    setAvailablePages(res?.data?.data ?? res?.data);
                } else {
                    toast.error(res?.message);
                }
            } catch (err) {
                toast.error(err?.message);
            }
        });
    };

    const handlePageConnect = () => {
        if (!selectedPageId) {
            toast.error("Please select a Facebook page", { position: 'top-center' });
            return;
        }

        setIsLoading(true);
        setCurrentStep('connecting');

        const data = {
            pageId: selectedPageId,
            businessId: businessId || "",
        };

        saveLeadPageId(authtoken, data)
            .then((res) => {
                if (res?.flag) {
                    setCurrentStep('success');
                    getUserApiCall();

                    setTimeout(() => {
                        toast.success("Facebook Leads connected successfully!", { position: 'top-center' });
                        setIsConnected(true);
                        // Move to BM creation step
                        setCurrentStep('createBM');
                        fetchPagesForChildBM();
                    }, 2000);
                } else {
                    toast.error(res?.message, { position: 'top-center' });
                    setCurrentStep('selectPage');
                }
            })
            .catch((error) => {
                console.error('Error connecting page:', error);
                toast.error("Failed to connect page", { position: 'top-center' });
                setCurrentStep('selectPage');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleConnectChildBM = async () => {
        if (!selectedPageForBM) {
            toast.error("Please select a Facebook page", { position: 'top-center' });
            return;
        }

        setIsLoading(true);
        setCurrentStep('bmConnecting');

        // Find the selected page to use its name
        const selectedPage = availablePages.find(page => page.id === selectedPageForBM);

        if (!selectedPage) {
            toast.error("Selected page not found", { position: 'top-center' });
            setIsLoading(false);
            setCurrentStep('createBM');
            return;
        }

        try {
            const childBMData = {
                bmName: `${selectedPage.name} Child BM`,
                shared_page_id: selectedPageForBM,
                adAccountName: `${selectedPage.name} Ad Account`,
            };

            const response = await onboardTwoTierBusiness(childBMData, authtoken);

            if (response?.flag) {
                setChildBMId(response.data?.id || '');
                setCurrentStep('bmSuccess');
                toast.success('Business Manager created successfully!', {
                    position: 'top-center',
                    autoClose: 3000,
                });

                // Refresh user data to get updated meta configuration
                getUserApiCall();

                // Navigate after success
                setTimeout(() => {
                    navigate('/creator/metaAdsManager');
                }, 2000);
            } else {
                toast.error(response?.message || 'Failed to create Business Manager', {
                    position: 'top-center'
                });
                setCurrentStep('createBM');
            }
        } catch (error) {
            console.error("Error creating Business Manager:", error);
            toast.error("An error occurred while creating Business Manager", { position: 'top-center' });
            setCurrentStep('createBM');
        } finally {
            setIsLoading(false);
        }
    };

    const getUserApiCall = () => {
        getUserApi(authtoken).then((res) => {
            if (res?.flag) {
                dispatch({ type: 'addLocaldata', payload: res?.data });
            }
        });
    };

    const renderConnectStep = () => (
        <div className="setup_modal">
            <h3 className="my-2">{'Connect your FB account'}</h3>
            <p className="w-100 my-3" style={{ color: colors.greys04 }}>
                {'To continue, You need to connect your Facebook business Account with Omnybox for further setup.'}
            </p>
            <h4 className="my-2" style={{ fontWeight: 600 }}>
                {'Why is this required?'}
            </h4>
            <p className="my-3">
                {`1. To access permissions to create and publish ads from your Facebook page`}
                <br />
                {`2. To receive advertisement analytics and events from Facebook`}
                <br />
                {`3. To setup Meta Pixel for ad performance optimization`}
            </p>
            <CheerioButton
                btnText={'Continue with Facebook'}
                textStyle={{ color: colors.white01 }}
                backColor={colors.primary03}
                borderStyle={{ width: '100%', marginBlock: '20px', borderColor: colors.primary03 }}
                onclick={handleFacebookConnect}
            />
            <p className="text-center" style={{ fontSize: '13px', color: colors.greys04 }}>
                Need help? <a href="#" style={{ color: colors.primary03 }}>Read Article</a> or{' '}
                <a href="#" style={{ color: colors.primary03 }}>Watch Video</a>
            </p>
        </div>
    );

    const renderSelectPageStep = () => (
        <div className="setup_modal">
            <h3 className="my-2">{'Select Facebook page'}</h3>
            <p className="w-100 my-3" style={{ color: colors.greys04 }}>
                {'To continue, You need to select a Facebook page which will be used to run an Ad.'}
            </p>
            <h4 className="my-2" style={{ fontWeight: 600 }}>
                {'Why is this required?'}
            </h4>
            <p className="my-3">
                {`1. To run ads, you will need to have a Facebook page where all your Ads will be posted.`}
            </p>

            <div className="my-3">
                <select
                    className="form-control"
                    value={selectedPageId}
                    onChange={(e) => {
                        setSelectedPageId(e.target.value);
                        // Extract business ID from selected page if needed
                        const selectedPage = availablePages.find(page => page.id === e.target.value);
                        if (selectedPage?.business) {
                            setBusinessId(selectedPage.business.id);
                        }
                    }}
                    style={{
                        width: '100%',
                        padding: '12px 15px',
                        borderRadius: '8px',
                        border: `1px solid ${colors.greys02}`,
                        fontSize: '14px',
                        backgroundColor: colors.white01,
                        cursor: 'pointer',
                        outline: 'none',
                    }}
                >
                    <option value="">Select an existing page</option>
                    {availablePages.map((page) => (
                        <option key={page.id} value={page.id}>
                            {page.name}
                        </option>
                    ))}
                </select>
            </div>

            <CheerioButton
                btnText={'Connect page'}
                textStyle={{ color: colors.white01 }}
                backColor={selectedPageId ? colors.primary03 : colors.greys02}
                borderStyle={{
                    width: '100%',
                    marginBlock: '20px',
                    borderColor: selectedPageId ? colors.primary03 : colors.greys02
                }}
                onclick={handlePageConnect}
                disabled={!selectedPageId || isLoading}
            />

            <p className="text-center" style={{ fontSize: '13px', color: colors.greys04 }}>
                Need help? <a href="#" style={{ color: colors.primary03 }}>Read Article</a> or{' '}
                <a href="#" style={{ color: colors.primary03 }}>Watch Video</a>
            </p>
        </div>
    );

    const renderConnectingStep = () => (
        <div className="setup_modal">
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div
                    className="spinner-border"
                    role="status"
                    style={{
                        width: '60px',
                        height: '60px',
                        color: colors.primary03,
                        marginBottom: '20px'
                    }}
                >
                    <span className="sr-only">Loading...</span>
                </div>
                <h3 className="my-3">{'Establishing Connection, Please wait'}</h3>
            </div>
        </div>
    );

    const renderSuccessStep = () => (
        <div className="setup_modal">
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div
                    style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        backgroundColor: '#00C853',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px',
                    }}
                >
                    <span style={{ fontSize: '48px', color: '#fff', fontWeight: 'bold' }}>✓</span>
                </div>
                <h3 className="my-3">{'Connection Successful'}</h3>
                <p style={{ color: colors.greys04 }}>
                    {'Facebook account connected! Now let\'s create your Business Manager.'}
                </p>
            </div>
        </div>
    );

    const renderCreateBMStep = () => (
        <div className="setup_modal">
            <h3 className="my-2">{'Connect Business Manager'}</h3>
            <p className="w-100 my-3" style={{ color: colors.greys04 }}>
                {'Create a Business Manager linked to your Facebook page to manage your ads effectively.'}
            </p>
            <h4 className="my-2" style={{ fontWeight: 600 }}>
                {'Why is this required?'}
            </h4>
            <p className="my-3">
                {`1. To create and manage ad campaigns professionally`}
                <br />
                {`2. To access advanced advertising features and analytics`}
                <br />
                {`3. To manage ad accounts and pages in one place`}
            </p>

            <div className="my-3">
                <select
                    className="form-control"
                    value={selectedPageForBM}
                    onChange={(e) => setSelectedPageForBM(e.target.value)}
                    style={{
                        width: '100%',
                        padding: '12px 15px',
                        borderRadius: '8px',
                        border: `1px solid ${colors.greys02}`,
                        fontSize: '14px',
                        backgroundColor: colors.white01,
                        cursor: 'pointer',
                        outline: 'none',
                    }}
                >
                    <option value="">Select Facebook Page</option>
                    {availablePages.map((page) => (
                        <option key={page.id} value={page.id}>
                            {page.name}
                        </option>
                    ))}
                </select>
            </div>

            <CheerioButton
                btnText={'Connect Business Manager'}
                textStyle={{ color: colors.white01 }}
                backColor={selectedPageForBM ? colors.primary03 : colors.greys02}
                borderStyle={{
                    width: '100%',
                    marginBlock: '20px',
                    borderColor: selectedPageForBM ? colors.primary03 : colors.greys02
                }}
                onclick={handleConnectChildBM}
                disabled={!selectedPageForBM || isLoading}
            />

            <p className="text-center" style={{ fontSize: '13px', color: colors.greys04 }}>
                Need help? <a href="#" style={{ color: colors.primary03 }}>Read Article</a> or{' '}
                <a href="#" style={{ color: colors.primary03 }}>Watch Video</a>
            </p>
        </div>
    );

    const renderBMConnectingStep = () => (
        <div className="setup_modal">
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div
                    className="spinner-border"
                    role="status"
                    style={{
                        width: '60px',
                        height: '60px',
                        color: colors.primary03,
                        marginBottom: '20px'
                    }}
                >
                    <span className="sr-only">Loading...</span>
                </div>
                <h3 className="my-3">{'Creating Business Manager, Please wait'}</h3>
                <p style={{ color: colors.greys04 }}>{'This may take a few moments...'}</p>
            </div>
        </div>
    );

    const renderBMSuccessStep = () => (
        <div className="setup_modal">
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div
                    style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        backgroundColor: '#00C853',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px',
                    }}
                >
                    <span style={{ fontSize: '48px', color: '#fff', fontWeight: 'bold' }}>✓</span>
                </div>
                <h3 className="my-3">{'Setup Complete!'}</h3>
                <p style={{ color: colors.greys04 }}>
                    {'Congratulations! Your Meta Ads Manager is now fully configured with Omnybox.'}
                </p>
                {childBMId && (
                    <p style={{ color: colors.greys04, fontSize: '12px', marginTop: '10px', marginBottom: '30px' }}>
                        Business Manager ID: {childBMId}
                    </p>
                )}
                {isConnected && (
                    <CheerioButton
                        btnText={'Disconnect Ads Manager'}
                        textStyle={{ color: colors.white01, fontSize: '14px' }}
                        backColor={colors.primary03}
                        borderStyle={{
                            borderColor: colors.primary03,
                            padding: '8px 16px',
                            borderRadius: '6px'
                        }}
                        onclick={handleDisconnect}
                    />
                )}
            </div>
        </div>
    );

    return (
        <div className="setup_container">
            <div className="setup_container__left verythinscrollnewinv">
                <div>
                    <div className="setup_container__left__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src={images.BackArrowBlack}
                                style={{ height: 20, width: 20, objectFit: 'contain', cursor: 'pointer', marginRight: '15px' }}
                                onClick={() => navigate(-1)}
                            />
                            <h3>{'Meta Ads Manager'}</h3>
                        </div>
                    </div>

                    <h4>{`What you'll need`}</h4>
                    <h4>{`Pre-installation`}</h4>
                    <p>{`To connect Meta Ads Manager on Omnybox. You will need to have an`}</p>
                    <p>
                        {`1. An active Facebook Business account`}
                        <br />
                        {`2. A Connected Facebook Ads Manager (To create and run an ad)`}
                        <br />
                        {`3. A Connected Events Manager (For Pixel setup)`}
                        <br />
                        {`4. A Connected Instagram Business Page (To receive incoming messages)`}
                        <br />
                        {`5. A Connected Facebook Business Page (To receive incoming messages)`}
                    </p>

                    <h4>{`What can I do with Meta Ads Manager on Omnybox?`}</h4>
                    <p>
                        {`1. You can create, run and publish click-to-Instagram DM Ads`}
                        <br />
                        {`2. You can create, run and publish click-to-Messenger DM Ads`}
                        <br />
                        {`3. You can create, run and publish Lead Form Ads`}
                        <br />
                        {`4. You can setup meta pixel for optimized Ads performance`}
                        <br />
                        {`5. You can track, manage & engage with leads directly through Inbox`}
                    </p>

                    <h4>{`Channel Capabilities`}</h4>
                    <p>
                        {`1. Images`}
                        <br />
                        {`2. Videos (Vertical/ Horizontal)`}
                        <br />
                        {`3. Text`}
                        <br />
                        {`4. Carousel`}
                        <br />
                        {`5. Links`}
                        <br />
                        {`6. Messages`}
                        <br />
                        {`7. Forms`}
                    </p>
                </div>
            </div>

            <div className="setup_container__right">
                {currentStep === 'connect' && renderConnectStep()}
                {currentStep === 'selectPage' && renderSelectPageStep()}
                {currentStep === 'connecting' && renderConnectingStep()}
                {currentStep === 'success' && renderSuccessStep()}
                {currentStep === 'createBM' && renderCreateBMStep()}
                {currentStep === 'bmConnecting' && renderBMConnectingStep()}
                {currentStep === 'bmSuccess' && renderBMSuccessStep()}
            </div>
        </div>
    );
};

export default SetupMetaAds;